import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, X, Package } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { brandSpecsManager, authManager } from '../mock';
import { useToast } from '../hooks/use-toast';
import UserNotesSystem from './UserNotesSystem';

const AddBrandSpecs = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Memoized handler for notes changes to prevent infinite loops
  const handleNotesChange = useCallback((notes) => {
    setFormData(prev => ({ ...prev, userNotes: notes }));
  }, []);
  // Generate Brand ID when component loads
  const [autoBrandId] = useState(() => {
    return `BID-${String(brandSpecsManager.nextBrandId).padStart(3, '0')}`;
  });

  const [formData, setFormData] = useState({
    brandName: '',
    brandCode: '',
    buyerCompany: '',
    contactPerson: '',
    clientPhone: '',
    clientEmail: '',
    clientAddress: '',
    
    // Bag Details
    bagSizeWidth: '',
    bagSizeLength: '',
    cuttingSizeWidth: '',
    cuttingSizeLength: '',
    top: 'Not Applicable',
    bottom: '',
    bagWeight: '',
    capacity: '',
    
    // PP Woven Fabric Specifications
    fabricVariant: '',
    fabricColor: '',
    fabricGrams: '',
    
    // BOPP Specifications
    printingMaterial: '',
    boppMicron: '',
    boppFilmSize: '',
    metallic: 'No',
    metallicMicrons: '',
    metallicFilmSize: '',
    window: 'No',
    windowMetallicFilmSize: '',
    
    // Other Details
    lamination: '',
    gusset: 'No',
    handle: 'No',
    perforation: 'No',
    
    // Cylinder Details
    printingAreaWidth: '',
    printingAreaCircum: '',
    cylinderSizeWidth: '',
    cylinderSizeCircum: '',
    numberOfCylinders: '',
    whiteCylinder: 'No',
    commonCylinder: '',
    
    // Artwork
    artworkImage: null,
    artworkImageUrl: '',
    artworkFileName: '',
    artworkFileType: '',
    
    // Status & Notes
    status: 'Active',
    userNotes: []
  });

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Please select a file smaller than 5MB.',
          variant: 'destructive'
        });
        return;
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid File Type',
          description: 'Please select an image (JPG, PNG, GIF) or PDF file.',
          variant: 'destructive'
        });
        return;
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          artworkImage: file,
          artworkImageUrl: event.target.result,
          artworkFileName: file.name,
          artworkFileType: file.type
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Special logic for fabric variant change
      if (field === 'fabricVariant') {
        if (value === 'Natural') {
          // When Natural variant is selected, fabric color must be Natural
          newData.fabricColor = 'Natural';
        } else {
          // When other variants are selected, reset window and remove Natural color lock
          newData.window = 'No';
          newData.windowMetallicFilmSize = '';
        }
      }
      
      // Special logic for metallic change
      if (field === 'metallic' && value === 'No') {
        newData.metallicMicrons = '';
        newData.metallicFilmSize = '';
      }
      
      // Special logic for window change
      if (field === 'window') {
        if (value === 'No') {
          newData.windowMetallicFilmSize = '';
        } else if (value === 'Yes' && newData.metallic === 'No') {
          // When Window is Yes but Metallic is No, automatically set to Not Applicable
          newData.windowMetallicFilmSize = 'Not Applicable';
        }
      }
      
      // Special logic for metallic change affecting window metallic film size
      if (field === 'metallic' && newData.window === 'Yes') {
        if (value === 'No') {
          // When Metallic is set to No but Window is Yes, automatically set to Not Applicable
          newData.windowMetallicFilmSize = 'Not Applicable';
        } else if (value === 'Yes' && newData.windowMetallicFilmSize === 'Not Applicable') {
          // When Metallic is set to Yes, clear the Not Applicable to allow size selection
          newData.windowMetallicFilmSize = '';
        }
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.brandName || !formData.brandCode || !formData.buyerCompany) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in Brand Name, Brand Code, and Buyer Company.',
        variant: 'destructive'
      });
      return;
    }

    // Bag details validation
    if (!formData.bottom) {
      toast({
        title: 'Validation Error',
        description: 'Please select a Bottom type in Bag Details.',
        variant: 'destructive'
      });
      return;
    }

    // Material specification validation
    if (!formData.fabricVariant || !formData.fabricColor || !formData.fabricGrams) {
      toast({
        title: 'Validation Error',
        description: 'Please complete all PP Woven Fabric details.',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.printingMaterial || !formData.boppMicron || !formData.boppFilmSize) {
      toast({
        title: 'Validation Error',
        description: 'Please complete all BOPP details including BOPP Film Size.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const brandData = {
        ...formData,
        brandId: autoBrandId, // Auto-generated Brand ID
        bagSizeWidth: parseFloat(formData.bagSizeWidth) || 0,
        bagSizeLength: parseFloat(formData.bagSizeLength) || 0,
        cuttingSizeWidth: parseFloat(formData.cuttingSizeWidth) || 0,
        cuttingSizeLength: parseFloat(formData.cuttingSizeLength) || 0,
        bagWeight: parseFloat(formData.bagWeight) || 0,
        fabricGSM: parseInt(formData.fabricGSM) || 0,
        // Cylinder details
        printingAreaWidth: parseFloat(formData.printingAreaWidth) || 0,
        printingAreaCircum: parseFloat(formData.printingAreaCircum) || 0,
        cylinderSizeWidth: parseFloat(formData.cylinderSizeWidth) || 0,
        cylinderSizeCircum: parseFloat(formData.cylinderSizeCircum) || 0,
        numberOfCylinders: parseInt(formData.numberOfCylinders) || 0,
        // Artwork - store base64 data URL for now (in production, upload to cloud storage)
        artworkImageUrl: formData.artworkImageUrl || '',
        // Notes system
        userNotes: formData.userNotes || [],
        createdDate: new Date().toISOString(),
        createdBy: authManager.getCurrentUser()?.name || 'Unknown User'
      };

      const newBrand = brandSpecsManager.addBrand(brandData);
      
      toast({
        title: 'Brand Added Successfully',
        description: `${newBrand.brandName} has been added to the specifications database.`,
      });

      navigate('/brand-specs');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add brand specification. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link to="/brand-specs">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Brand Specs
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Add New Brand Specification</h1>
              <p className="text-gray-600">Create BOPP bag specifications for a new brand</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Client Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Package className="w-5 h-5 mr-2" />
                  Client Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Auto-generated Brand ID */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium text-blue-900">Auto-Generated Brand ID</Label>
                      <p className="text-lg font-bold text-blue-700">{autoBrandId}</p>
                    </div>
                    <div className="text-xs text-blue-600">
                      Unique identifier for this brand
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="brandName">Brand Name *</Label>
                    <Input
                      id="brandName"
                      type="text"
                      placeholder="Enter brand name"
                      value={formData.brandName}
                      onChange={(e) => handleInputChange('brandName', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="brandCode">Brand Code *</Label>
                    <Input
                      id="brandCode"
                      type="text"
                      placeholder="Enter brand code"
                      value={formData.brandCode}
                      onChange={(e) => handleInputChange('brandCode', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="buyerCompany">Client/Buyer Company *</Label>
                  <Input
                    id="buyerCompany"
                    type="text"
                    placeholder="Enter buyer company name"
                    value={formData.buyerCompany}
                    onChange={(e) => handleInputChange('buyerCompany', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPerson">Contact Person</Label>
                  <Input
                    id="contactPerson"
                    type="text"
                    placeholder="Enter contact person name"
                    value={formData.contactPerson || ''}
                    onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="clientPhone">Phone Number</Label>
                    <Input
                      id="clientPhone"
                      type="tel"
                      placeholder="Enter phone number"
                      value={formData.clientPhone || ''}
                      onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clientEmail">Email Address</Label>
                    <Input
                      id="clientEmail"
                      type="email"
                      placeholder="Enter email address"
                      value={formData.clientEmail || ''}
                      onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientAddress">Client Address</Label>
                  <Textarea
                    id="clientAddress"
                    placeholder="Complete client address"
                    value={formData.clientAddress || ''}
                    onChange={(e) => handleInputChange('clientAddress', e.target.value)}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Bag Details */}
            <Card>
              <CardHeader>
                <CardTitle>Bag Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Bag Size */}
                <div className="space-y-2">
                  <Label className="text-base font-medium">Bag Size (Inches) *</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bagSizeWidth">Width</Label>
                      <Select value={formData.bagSizeWidth || ''} onValueChange={(value) => handleInputChange('bagSizeWidth', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select width" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="12">12</SelectItem>
                          <SelectItem value="14">14</SelectItem>
                          <SelectItem value="14.5">14.5</SelectItem>
                          <SelectItem value="15">15</SelectItem>
                          <SelectItem value="16">16</SelectItem>
                          <SelectItem value="17">17</SelectItem>
                          <SelectItem value="18">18</SelectItem>
                          <SelectItem value="19">19</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="21">21</SelectItem>
                          <SelectItem value="22">22</SelectItem>
                          <SelectItem value="24">24</SelectItem>
                          <SelectItem value="26">26</SelectItem>
                          <SelectItem value="28">28</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="bagSizeLength">Length</Label>
                      <Input
                        id="bagSizeLength"
                        type="number"
                        step="0.1"
                        value={formData.bagSizeLength || ''}
                        onChange={(e) => handleInputChange('bagSizeLength', e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">Final bag dimensions: Width × Length</p>
                </div>

                {/* Cutting Size */}
                <div className="space-y-2">
                  <Label className="text-base font-medium">Cutting Size (Inches) *</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cuttingSizeWidth">Width</Label>
                      <Input
                        id="cuttingSizeWidth"
                        type="number"
                        step="0.1"
                        value={formData.cuttingSizeWidth || ''}
                        onChange={(e) => handleInputChange('cuttingSizeWidth', e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="cuttingSizeLength">Length</Label>
                      <Input
                        id="cuttingSizeLength"
                        type="number"
                        step="0.1"
                        value={formData.cuttingSizeLength || ''}
                        onChange={(e) => handleInputChange('cuttingSizeLength', e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">Fabric cutting dimensions: Width × Length</p>
                </div>

                {/* Top */}
                <div className="space-y-2">
                  <Label htmlFor="top">Top *</Label>
                  <Select value={formData.top || 'Not Applicable'} onValueChange={(value) => handleInputChange('top', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Not Applicable">Not Applicable</SelectItem>
                      <SelectItem value="Top Hemming">Top Hemming</SelectItem>
                      <SelectItem value="Top Hemming with Handle">Top Hemming with Handle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Additional Bag Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bottom">Bottom *</Label>
                    <Select value={formData.bottom || ''} onValueChange={(value) => handleInputChange('bottom', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select bottom type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Single fold double stitch">Single fold double stitch</SelectItem>
                        <SelectItem value="Double Fold Double Stitch">Double Fold Double Stitch</SelectItem>
                        <SelectItem value="No Fold Single Stitch">No Fold Single Stitch</SelectItem>
                        <SelectItem value="No Fold Double Stitch">No Fold Double Stitch</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bagWeight">Desired Bag Weight (grams)</Label>
                    <Input
                      id="bagWeight"
                      type="number"
                      step="1"
                      placeholder="125"
                      value={formData.bagWeight}
                      onChange={(e) => handleInputChange('bagWeight', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="capacity">Capacity</Label>
                    <Input
                      id="capacity"
                      type="text"
                      value={formData.capacity}
                      onChange={(e) => handleInputChange('capacity', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Material Specifications */}
            <Card>
              <CardHeader>
                <CardTitle>Material Specifications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* PP Woven Fabric Details */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">PP Woven Fabric Details</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fabricWidth">Fabric Width (inches)</Label>
                      <Input
                        id="fabricWidth"
                        type="number"
                        step="0.1"
                        value={formData.bagSizeWidth || ''}
                        disabled
                        className="bg-gray-100"
                      />
                      <p className="text-xs text-gray-500">Same as Bag Width</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="fabricVariant">Fabric Variant</Label>
                      <Select value={formData.fabricVariant || ''} onValueChange={(value) => handleInputChange('fabricVariant', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select fabric variant" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CF">CF</SelectItem>
                          <SelectItem value="Natural">Natural</SelectItem>
                          <SelectItem value="ECO">ECO</SelectItem>
                          <SelectItem value="SP">SP</SelectItem>
                          <SelectItem value="SSP">SSP</SelectItem>
                          <SelectItem value="Export SP">Export SP</SelectItem>
                          <SelectItem value="Others">Others</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="fabricColor">Fabric Color</Label>
                      {formData.fabricVariant === 'Natural' ? (
                        <div>
                          <Input
                            value="Natural"
                            disabled
                            className="bg-gray-100 text-gray-700 font-medium"
                          />
                          <p className="text-xs text-gray-500 mt-1">Automatically set to Natural when Natural variant is selected</p>
                        </div>
                      ) : (
                        <Select value={formData.fabricColor || ''} onValueChange={(value) => handleInputChange('fabricColor', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select fabric color" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="White">White</SelectItem>
                            <SelectItem value="Green">Green</SelectItem>
                            <SelectItem value="Pink">Pink</SelectItem>
                            <SelectItem value="Red">Red</SelectItem>
                            <SelectItem value="Yellow">Yellow</SelectItem>
                            <SelectItem value="Blue">Blue</SelectItem>
                            <SelectItem value="Parrot Green">Parrot Green</SelectItem>
                            <SelectItem value="Weft Green">Weft Green</SelectItem>
                            <SelectItem value="Weft Pink">Weft Pink</SelectItem>
                            <SelectItem value="Weft Red">Weft Red</SelectItem>
                            <SelectItem value="Weft Yellow">Weft Yellow</SelectItem>
                            <SelectItem value="Weft Blue">Weft Blue</SelectItem>
                            <SelectItem value="Weft Parrot Green">Weft Parrot Green</SelectItem>
                            <SelectItem value="Ivory">Ivory</SelectItem>
                            <SelectItem value="Natural Border">Natural Border</SelectItem>
                            <SelectItem value="Half Yellow">Half Yellow</SelectItem>
                            <SelectItem value="Half White with natural">Half White with natural</SelectItem>
                            <SelectItem value="Half Blue with Natural">Half Blue with Natural</SelectItem>
                            <SelectItem value="Pink Border">Pink Border</SelectItem>
                            <SelectItem value="Natural">Natural</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="fabricGrams">Fabric Grams</Label>
                      <Select value={formData.fabricGrams || ''} onValueChange={(value) => handleInputChange('fabricGrams', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select fabric grams" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2.25">2.25</SelectItem>
                          <SelectItem value="2.5">2.5</SelectItem>
                          <SelectItem value="3">3</SelectItem>
                          <SelectItem value="3.25">3.25</SelectItem>
                          <SelectItem value="3.5">3.5</SelectItem>
                          <SelectItem value="3.6">3.6</SelectItem>
                          <SelectItem value="3.75">3.75</SelectItem>
                          <SelectItem value="4">4</SelectItem>
                          <SelectItem value="4.25">4.25</SelectItem>
                          <SelectItem value="4.5">4.5</SelectItem>
                          <SelectItem value="5">5</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* BOPP Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">BOPP Details</h3>
                  
                  {/* Row 1: Printing Material & BOPP Film Size */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="printingMaterial">Printing Material</Label>
                      <Select value={formData.printingMaterial || ''} onValueChange={(value) => handleInputChange('printingMaterial', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select printing material" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Glossy BOPP">Glossy BOPP</SelectItem>
                          <SelectItem value="Matte BOPP">Matte BOPP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="boppFilmSize">BOPP Film Size</Label>
                      <Select value={formData.boppFilmSize || ''} onValueChange={(value) => handleInputChange('boppFilmSize', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select BOPP film size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="330">330</SelectItem>
                          <SelectItem value="380">380</SelectItem>
                          <SelectItem value="430">430</SelectItem>
                          <SelectItem value="510">510</SelectItem>
                          <SelectItem value="515">515</SelectItem>
                          <SelectItem value="520">520</SelectItem>
                          <SelectItem value="540">540</SelectItem>
                          <SelectItem value="560">560</SelectItem>
                          <SelectItem value="590">590</SelectItem>
                          <SelectItem value="620">620</SelectItem>
                          <SelectItem value="640">640</SelectItem>
                          <SelectItem value="680">680</SelectItem>
                          <SelectItem value="730">730</SelectItem>
                          <SelectItem value="760">760</SelectItem>
                          <SelectItem value="780">780</SelectItem>
                          <SelectItem value="860">860</SelectItem>
                          <SelectItem value="920">920</SelectItem>
                          <SelectItem value="1020">1020</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 2: BOPP Micron & Metallic */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="boppMicron">BOPP Micron</Label>
                      <Select value={formData.boppMicron || ''} onValueChange={(value) => handleInputChange('boppMicron', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select BOPP micron" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="12">12</SelectItem>
                          <SelectItem value="15">15</SelectItem>
                          <SelectItem value="18">18</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="metallic">Metallic</Label>
                      <Select value={formData.metallic || 'No'} onValueChange={(value) => handleInputChange('metallic', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 3: Metallic Film Size & Metallic Microns */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="metallicFilmSize">Metallic Film Size</Label>
                      {formData.metallic === 'Yes' ? (
                        <Select value={formData.metallicFilmSize || ''} onValueChange={(value) => handleInputChange('metallicFilmSize', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select metallic film size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="330">330</SelectItem>
                            <SelectItem value="380">380</SelectItem>
                            <SelectItem value="430">430</SelectItem>
                            <SelectItem value="510">510</SelectItem>
                            <SelectItem value="515">515</SelectItem>
                            <SelectItem value="520">520</SelectItem>
                            <SelectItem value="540">540</SelectItem>
                            <SelectItem value="560">560</SelectItem>
                            <SelectItem value="590">590</SelectItem>
                            <SelectItem value="620">620</SelectItem>
                            <SelectItem value="640">640</SelectItem>
                            <SelectItem value="680">680</SelectItem>
                            <SelectItem value="730">730</SelectItem>
                            <SelectItem value="760">760</SelectItem>
                            <SelectItem value="780">780</SelectItem>
                            <SelectItem value="860">860</SelectItem>
                            <SelectItem value="920">920</SelectItem>
                            <SelectItem value="1020">1020</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value="Not Applicable"
                          disabled
                          className="bg-gray-100 text-gray-500"
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="metallicMicrons">Metallic Microns</Label>
                      {formData.metallic === 'Yes' ? (
                        <Select value={formData.metallicMicrons || ''} onValueChange={(value) => handleInputChange('metallicMicrons', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select metallic microns" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="8">8</SelectItem>
                            <SelectItem value="15">15</SelectItem>
                            <SelectItem value="18">18</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value="Not Applicable"
                          disabled
                          className="bg-gray-100 text-gray-500"
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="window">Window</Label>
                      <Select 
                        value={formData.window || 'No'} 
                        onValueChange={(value) => handleInputChange('window', value)}
                        disabled={formData.fabricVariant !== 'Natural'}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.fabricVariant !== 'Natural' && (
                        <p className="text-xs text-gray-500">Window option only available for Natural Fabric</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="windowMetallicFilmSize">Window Metallic Film Size</Label>
                      {formData.window === 'Yes' && formData.fabricVariant === 'Natural' ? (
                        <Select value={formData.windowMetallicFilmSize || ''} onValueChange={(value) => handleInputChange('windowMetallicFilmSize', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select film size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Not Applicable">Not Applicable</SelectItem>
                            <SelectItem value="400+115">400+115</SelectItem>
                            <SelectItem value="250+85">250+85</SelectItem>
                            <SelectItem value="330+105">330+105</SelectItem>
                            <SelectItem value="385+110">385+110</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value="Not Applicable"
                          disabled
                          className="bg-gray-100 text-gray-500"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Other Details */}
            <Card>
              <CardHeader>
                <CardTitle>Other Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lamination">Lamination</Label>
                    <Select value={formData.lamination || ''} onValueChange={(value) => handleInputChange('lamination', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select lamination type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Both Side BOPP Lamination">Both Side BOPP Lamination</SelectItem>
                        <SelectItem value="Single Side BOPP other side unlam">Single Side BOPP other side unlam</SelectItem>
                        <SelectItem value="Single Side BOPP other side Lam">Single Side BOPP other side Lam</SelectItem>
                        <SelectItem value="Single Side BOPP other side unlam with flex print">Single Side BOPP other side unlam with flex print</SelectItem>
                        <SelectItem value="Single Side BOPP other side Lam with flex print">Single Side BOPP other side Lam with flex print</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gusset">Gusset</Label>
                    <Select value={formData.gusset || 'No'} onValueChange={(value) => handleInputChange('gusset', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="handle">Handle</Label>
                    <Select value={formData.handle || 'No'} onValueChange={(value) => handleInputChange('handle', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Top Hem">Top Hem</SelectItem>
                        <SelectItem value="Ultra Sound">Ultra Sound</SelectItem>
                        <SelectItem value="D Punch">D Punch</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="perforation">Perforation</Label>
                    <Select value={formData.perforation || 'No'} onValueChange={(value) => handleInputChange('perforation', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cylinder Details */}
            <Card>
              <CardHeader>
                <CardTitle>Cylinder Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="printingAreaWidth">Printing Area Width (mm)</Label>
                    <Input
                      id="printingAreaWidth"
                      type="number"
                      step="0.1"
                      placeholder="Enter width in mm"
                      value={formData.printingAreaWidth}
                      onChange={(e) => handleInputChange('printingAreaWidth', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="printingAreaCircum">Printing Area Circumference (mm)</Label>
                    <Input
                      id="printingAreaCircum"
                      type="number"
                      step="0.1"
                      placeholder="Enter circumference in mm"
                      value={formData.printingAreaCircum}
                      onChange={(e) => handleInputChange('printingAreaCircum', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cylinderSizeWidth">Cylinder Size Width (mm)</Label>
                    <Input
                      id="cylinderSizeWidth"
                      type="number"
                      step="0.1"
                      placeholder="Enter width in mm"
                      value={formData.cylinderSizeWidth}
                      onChange={(e) => handleInputChange('cylinderSizeWidth', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cylinderSizeCircum">Cylinder Size Circumference (mm)</Label>
                    <Input
                      id="cylinderSizeCircum"
                      type="number"
                      step="0.1"
                      placeholder="Enter circumference in mm"
                      value={formData.cylinderSizeCircum}
                      onChange={(e) => handleInputChange('cylinderSizeCircum', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="numberOfCylinders">No. of Cylinders</Label>
                    <Input
                      id="numberOfCylinders"
                      type="number"
                      min="0"
                      placeholder="Enter number"
                      value={formData.numberOfCylinders}
                      onChange={(e) => handleInputChange('numberOfCylinders', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="whiteCylinder">White Cylinder</Label>
                    <Select value={formData.whiteCylinder || 'No'} onValueChange={(value) => handleInputChange('whiteCylinder', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Registered White">Registered White</SelectItem>
                        <SelectItem value="Full Coat White">Full Coat White</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="commonCylinder">Common Cylinder</Label>
                    <Input
                      id="commonCylinder"
                      type="text"
                      placeholder="Enter common cylinder details"
                      value={formData.commonCylinder}
                      onChange={(e) => handleInputChange('commonCylinder', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Artwork Upload */}
            <Card>
              <CardHeader>
                <CardTitle>Artwork Upload</CardTitle>
                <p className="text-sm text-gray-600">Upload the artwork for this brand name (optional)</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="artworkImage">Brand Artwork</Label>
                  <Input
                    id="artworkImage"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => handleImageUpload(e)}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500">
                    Supported formats: JPG, PNG, GIF, PDF. Max size: 5MB
                  </p>
                  {formData.artworkImageUrl && (
                    <div className="mt-2">
                      {formData.artworkFileType === 'application/pdf' ? (
                        <div className="flex items-center p-3 border rounded bg-gray-50">
                          <svg className="w-8 h-8 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V6H8l-2-2z" clipRule="evenodd"/>
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{formData.artworkFileName}</p>
                            <p className="text-xs text-gray-500">PDF File</p>
                          </div>
                        </div>
                      ) : (
                        <img 
                          src={formData.artworkImageUrl} 
                          alt="Artwork preview" 
                          className="max-w-xs max-h-40 object-contain border rounded"
                        />
                      )}
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        className="mt-2"
                        onClick={() => handleInputChange('artworkImageUrl', '')}
                      >
                        Remove File
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Status */}
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Additional Details - Notes System */}
            <Card>
              <CardHeader>
                <CardTitle>Additional Details</CardTitle>
                <p className="text-sm text-gray-600">Add notes and comments for this brand specification</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <UserNotesSystem 
                  brandId="new-brand-form"
                  onNotesChange={handleNotesChange}
                  initialNotes={formData.userNotes || []}
                />
              </CardContent>
            </Card>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding Brand...
                  </div>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Add Brand Specification
                  </>
                )}
              </Button>
              <Link to="/brand-specs" className="flex-1">
                <Button
                  type="button"
                  variant="outline"
                  disabled={loading}
                  className="w-full"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBrandSpecs;